import { useEffect, useState } from 'react'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { Loader2 } from 'lucide-react'
import { algorithmicScannerService } from '@/services/algorithmicScanner'
import SignalIndicatorRanking from './SignalIndicatorRanking'
import SignalAssetRanking from './SignalAssetRanking'
import SignalTimeframeRanking from './SignalTimeframeRanking'
import SignalComboRanking from './SignalComboRanking'
import SlRecommendations from './SlRecommendations'

function pct(v) {
  return v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`
}

function num(v, digits = 2) {
  return v === null || v === undefined ? '—' : v.toFixed(digits)
}

const TONE = {
  good: 'text-emerald-600 dark:text-emerald-400',
  bad: 'text-red-500 dark:text-red-400',
  neutral: 'text-slate-800 dark:text-gray-200',
}

function StatTile({ label, value, tone = 'neutral' }) {
  return (
    <div className="bg-slate-50 dark:bg-gray-800 rounded-lg p-3">
      <p className="text-[10px] text-slate-500 dark:text-gray-400 uppercase">{label}</p>
      <p className={`text-lg font-semibold ${TONE[tone]}`}>{value}</p>
    </div>
  )
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  return (
    <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="text-slate-500 dark:text-gray-400">{new Date(p.signal_time).toLocaleString('es')}</p>
      <p className="font-semibold text-slate-800 dark:text-gray-200">{p.cumulative_r.toFixed(2)}R</p>
    </div>
  )
}

function EquityCurveChart({ data }) {
  if (!data || data.length < 2) {
    return <p className="text-sm text-slate-500 dark:text-gray-400 text-center py-8">Curva insuficiente todavía (hacen falta al menos 2 señales resueltas)</p>
  }
  const isPositive = data[data.length - 1].cumulative_r >= 0
  const color = isPositive ? '#10b981' : '#ef4444'
  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
        <defs>
          <linearGradient id="gradEquityR" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.25} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
        <XAxis
          dataKey="signal_time"
          tickFormatter={(v) => new Date(v).toLocaleDateString('es', { day: 'numeric', month: 'short' })}
          tick={{ fontSize: 10, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 10, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v) => `${v.toFixed(0)}R`}
          width={40}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="cumulative_r"
          stroke={color}
          strokeWidth={2}
          fill="url(#gradEquityR)"
          dot={false}
          activeDot={{ r: 4 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

export default function SignalPerformanceDashboard({ scannerId = null }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    load()
  }, [scannerId])

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: res } = scannerId
        ? await algorithmicScannerService.getScannerDashboard(scannerId)
        : await algorithmicScannerService.getGlobalDashboard()
      setData(res)
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar el dashboard de rentabilidad')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
  }
  if (error) {
    return <p className="text-sm text-red-500 dark:text-red-400 text-center py-8">{error}</p>
  }

  const stats = data?.stats
  if (!stats || (stats.total_signals === 0 && stats.pending_signals === 0)) {
    return (
      <div className="card text-center py-12">
        <p className="text-sm text-slate-500 dark:text-gray-400">
          Todavía no hay señales registradas para este escáner.
        </p>
      </div>
    )
  }

  const winRateTone = stats.win_rate >= 0.5 ? 'good' : stats.win_rate < 0.4 ? 'bad' : 'neutral'
  const pfTone = stats.profit_factor >= 1.5 ? 'good' : stats.profit_factor !== null && stats.profit_factor < 1 ? 'bad' : 'neutral'
  const ddTone = stats.current_drawdown_r > 3 ? 'bad' : 'neutral'

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatTile label="Señales" value={stats.total_signals} />
        <StatTile label="Win Rate" value={pct(stats.win_rate)} tone={winRateTone} />
        <StatTile label="% SL" value={pct(stats.pct_sl)} tone={stats.pct_sl > 0.5 ? 'bad' : 'neutral'} />
        <StatTile label="% TP1" value={pct(stats.pct_tp1)} />
        <StatTile label="% TP2" value={pct(stats.pct_tp2)} />
        <StatTile label="% TP3" value={pct(stats.pct_tp3)} />
        <StatTile label="Profit Factor" value={num(stats.profit_factor)} tone={pfTone} />
        <StatTile label="R:R" value={num(stats.rr_ratio)} />
        <StatTile label="Máx. Pérdidas Consecutivas" value={stats.max_consecutive_losses} tone={stats.max_consecutive_losses >= 5 ? 'bad' : 'neutral'} />
        <StatTile label="Sharpe" value={num(stats.sharpe_ratio)} />
        <StatTile label="Drawdown Actual" value={`${num(stats.current_drawdown_r)}R`} tone={ddTone} />
      </div>

      {stats.resolved_signals > 0 && (
        <div className="card">
          <h3 className="section-title mb-2">Curva de equity (R acumulado)</h3>
          <EquityCurveChart data={data.equity_curve} />
        </div>
      )}

      <div className="card">
        <SignalIndicatorRanking scannerId={scannerId} />
      </div>

      <div className="card">
        <SignalAssetRanking scannerId={scannerId} />
      </div>

      <div className="card">
        <SignalTimeframeRanking scannerId={scannerId} />
      </div>

      <div className="card">
        <SignalComboRanking scannerId={scannerId} />
      </div>

      <div className="card">
        <SlRecommendations scannerId={scannerId} />
      </div>
    </div>
  )
}
